import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Mail, Lock, HelpCircle, CheckCircle2 } from "lucide-react";

import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../state/auth";

export default function LoginPage() {
  const nav = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      const from = location.state?.from?.pathname || "/wizard";
      nav(from, { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-white">
      <Header />
      <div className="max-w-md mx-auto px-6 lg:px-8 pt-16 pb-24">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#c80f3f]">
          Team access
        </div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#1f2a44] tracking-tight">
          Sign in
        </h1>
        <p className="mt-3 text-sm text-[#4b5563] leading-relaxed">
          Access is restricted to authorised team members and partners. If you
          need an account, contact your Helvar administrator.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4b5563]">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4b5563]" strokeWidth={1.5} />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                required
                className="pl-9 rounded-sm border-[#e5e7eb] focus-visible:ring-[#1f2a44]"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4b5563]">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4b5563]" strokeWidth={1.5} />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                required
                className="pl-9 rounded-sm border-[#e5e7eb] focus-visible:ring-[#1f2a44]"
              />
            </div>
          </div>

          {err && (
            <div data-testid="login-error" className="text-xs text-[#c80f3f] border border-[#c80f3f]/40 bg-[#c80f3f]/5 rounded-sm px-3 py-2">
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full bg-[#1f2a44] hover:bg-[#161e31] text-white rounded-sm py-6"
          >
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…</> : "Sign in"}
          </Button>
        </form>

        <ForgotPasswordPanel defaultEmail={email} />

        <p className="mt-6 text-xs text-[#4b5563]">
          <Link to="/" className="hover:text-[#1f2a44] underline underline-offset-2">
            ← Back to the landing page
          </Link>
        </p>
      </div>
    </div>
  );
}

function ForgotPasswordPanel({ defaultEmail }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState(null);

  const openPanel = () => {
    setEmail(defaultEmail || "");
    setErr(null);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-help", {
        email: email.trim().toLowerCase(),
        message: note.trim() || undefined,
      });
      setSent(data.message);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="forgot-password-sent"
        className="mt-6 flex items-start gap-3 border-l-2 border-[#1f2a44] bg-[#f4f5f7] rounded-sm px-4 py-3 text-sm text-[#1f2937] leading-relaxed"
      >
        <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#1f2a44] shrink-0" strokeWidth={1.75} />
        <span>{sent}</span>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={openPanel}
        data-testid="forgot-password-link"
        className="mt-5 flex items-center gap-2 text-xs font-semibold text-[#c80f3f] hover:text-[#a60c34] underline underline-offset-2"
      >
        <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
        Forgotten your password?
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="forgot-password-form"
      className="mt-6 border border-[#e5e7eb] rounded-sm p-5 space-y-4"
    >
      <div>
        <div className="text-sm font-semibold text-[#1f2a44]">Request help signing in</div>
        <p className="mt-1 text-xs text-[#4b5563] leading-relaxed">
          Enter your email address and the Helvar team will review it and get back
          to you — whether you need a new password or do not have access yet.
        </p>
      </div>
      <div className="space-y-2">
        <Label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4b5563]">
          Your email
        </Label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="forgot-password-email"
          className="rounded-sm border-[#e5e7eb] h-11"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4b5563]">
          Anything we should know? (optional)
        </Label>
        <Input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
          placeholder="e.g. my company has changed"
          data-testid="forgot-password-note"
          className="rounded-sm border-[#e5e7eb] h-11"
        />
      </div>

      {err && (
        <div
          data-testid="forgot-password-error"
          className="text-xs text-[#c80f3f] border border-[#c80f3f]/40 bg-[#c80f3f]/5 rounded-sm px-3 py-2"
        >
          {err}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button
          type="submit"
          disabled={busy}
          data-testid="forgot-password-submit"
          className="bg-[#c80f3f] hover:bg-[#a60c34] text-white rounded-sm h-10 px-5 text-xs uppercase tracking-[0.2em] font-semibold"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send request"}
        </Button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="forgot-password-cancel"
          className="text-xs text-[#4b5563] hover:text-[#1f2a44] underline underline-offset-2"
        >
          Cancel
        </button>
      </div>
      <p className="text-[11px] text-[#4b5563] leading-relaxed">
        We never send passwords by email. An administrator will set a new one for
        you and confirm it directly.
      </p>
    </form>
  );
}
